import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext } from "./api";
import { alsStunden } from "./datum";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Textknopf } from "./ui";

/**
 * Die Sollstunden am Objekt.
 *
 * Je Abteilung, was dieses Objekt in der Woche braucht: Garten vier Stunden,
 * Pool eine. Gerechnet wird in **Personenstunden**, nicht in Uhrzeit am
 * Kalender: vier Stunden Garten sind erfuellt durch einen Gaertner vier
 * Stunden lang oder durch zwei Gaertner zwei Stunden lang.
 *
 * Die Gueltigkeit ist ein Zeitraum und keine einzelne Zahl, weil auf Mallorca
 * der Pool im August anders gepflegt wird als im Januar. Zwei Zeilen mit
 * Zeitraum sind ehrlicher als ein Wert, den zweimal im Jahr jemand von Hand
 * umschreiben muesste und der dazwischen falsch ist. Zwei Zeilen fuer
 * dieselbe Abteilung duerfen sich deshalb nicht ueberschneiden.
 */

interface Leistung {
  id: string;
  abteilungId: string;
  abteilung: string;
  minutenProWoche: number;
  gueltigVon: string | null;
  gueltigBis: string | null;
  /** Fester Wochentag nach ISO, 1 ist Montag. Null: frei planbar. */
  festerTag: number | null;
  /** Feste Anfangszeit in Minuten seit Mitternacht. Nur mit festem Tag. */
  festeStartminute: number | null;
  /** Alle wieviel Wochen die Leistung faellig ist. Eins ist der Normalfall. */
  rhythmusWochen: number;
  /** Der Montag, ab dem gezaehlt wird. Nur bei einem Rhythmus ueber einer Woche. */
  rhythmusAb: string | null;
  notiz: string | null;
}

/** Die Wochentage in der Reihenfolge, in der jeder sie kennt. */
const WOCHENTAGE = [1, 2, 3, 4, 5, 6, 7];

/**
 * Mehr als vier Wochen waere kein Rhythmus mehr.
 *
 * Wer ein Haus alle zwei Monate anfaehrt, traegt den Termin ein, statt die
 * Anlage rechnen zu lassen: bei so grossen Abstaenden faellt eine vergessene
 * Woche erst auf, wenn der Garten schon steht.
 */
const RHYTHMEN = [1, 2, 3, 4];

/** Der Montag der Woche, in der dieses Datum liegt. */
function montagDerWoche(datum: Date): string {
  const d = new Date(Date.UTC(datum.getFullYear(), datum.getMonth(), datum.getDate()));
  // getUTCDay liefert 0 fuer Sonntag; der Montag liegt dann sechs Tage zurueck.
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

/** 540 wird zu "09:00". */
function alsUhrzeit(minuten: number): string {
  return `${String(Math.floor(minuten / 60)).padStart(2, "0")}:${String(minuten % 60).padStart(2, "0")}`;
}

interface Abteilung {
  id: string;
  name: string;
}

export function SollstundenAmObjekt({
  objektId, darfAendern
}: { objektId: string; darfAendern: boolean }) {
  const { t, i18n } = useTranslation();
  const [leistungen, setLeistungen] = useState<Leistung[] | null>(null);
  const [abteilungen, setAbteilungen] = useState<Abteilung[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);

  function laden() {
    api.get<{ leistungen: Leistung[] }>(`/properties/${objektId}/leistungen`)
      .then((a) => { setLeistungen(a.leistungen); setFehler(null); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, [objektId]);

  useEffect(() => {
    void api.get<{ abteilungen: Abteilung[] }>("/abteilungen")
      .then((a) => setAbteilungen(a.abteilungen)).catch(() => setAbteilungen([]));
  }, []);

  async function loeschen(id: string) {
    try {
      await api.del(`/leistungen/${id}`);
      laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  const gesamt = (leistungen ?? []).reduce((summe, l) => summe + l.minutenProWoche, 0);

  return (
    <Karte>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="font-bold">{t("leistung.titel")}</h2>
        {gesamt > 0 && (
          <span className="text-sm text-leise tabular-nums">
            {t("leistung.gesamt", { stunden: alsStunden(gesamt) })}
          </span>
        )}
      </div>
      <Fehler text={fehler} />

      {!leistungen ? <Laedt /> : leistungen.length === 0 ? (
        <p className="text-sm text-leise">{t("leistung.leer")}</p>
      ) : (
        <ul className="space-y-2">
          {leistungen.map((l) => (
            <li
              key={l.id}
              className="flex items-start justify-between gap-3 border-b border-linie pb-2 last:border-0"
            >
              <div className="min-w-0">
                <p className="font-semibold">
                  {l.abteilung}
                  <span className="ms-2 font-normal tabular-nums">
                    {alsStunden(l.minutenProWoche)}
                  </span>
                </p>
                {(l.gueltigVon || l.gueltigBis) && (
                  <p className="text-xs text-leise">
                    {zeitraum(l.gueltigVon, l.gueltigBis, i18n.language, t)}
                  </p>
                )}
                {/* Ein Festtermin faellt auf, sonst waere er keiner. */}
                {l.festerTag !== null && (
                  <p className="text-xs font-semibold text-verweis">
                    {l.festeStartminute !== null
                      ? t("leistung.festTagZeit", {
                          tag: t(`wochentag.${l.festerTag}`),
                          zeit: alsUhrzeit(l.festeStartminute)
                        })
                      : t("leistung.festNurTag", { tag: t(`wochentag.${l.festerTag}`) })}
                  </p>
                )}
                {/*
                  * Ein Rhythmus ueber einer Woche muss stehen, sonst liest
                  * jemand die Wochenzeit als Wochenzeit.
                  */}
                {l.rhythmusWochen > 1 && (
                  <p className="text-xs font-semibold text-verweis">
                    {t("leistung.rhythmusZeile", {
                      wochen: l.rhythmusWochen,
                      ab: l.rhythmusAb ? kurzesDatum(l.rhythmusAb, i18n.language) : ""
                    })}
                  </p>
                )}
                {l.notiz && <p className="text-xs">{l.notiz}</p>}
              </div>
              {darfAendern && (
                <Textknopf
                  gefahr
                  onClick={() => {
                    if (window.confirm(t("leistung.loeschenSicher"))) void loeschen(l.id);
                  }}
                >
                  {t("aktion.loeschen")}
                </Textknopf>
              )}
            </li>
          ))}
        </ul>
      )}

      {darfAendern && abteilungen.length > 0 && (
        neu ? (
          <LeistungFormular
            objektId={objektId}
            abteilungen={abteilungen}
            onAbbruch={() => setNeu(false)}
            onFertig={() => { setNeu(false); laden(); }}
          />
        ) : (
          <div className="mt-3">
            <Knopf art="neben" onClick={() => setNeu(true)}>{t("leistung.neu")}</Knopf>
          </div>
        )
      )}
    </Karte>
  );
}

function LeistungFormular({
  objektId, abteilungen, onAbbruch, onFertig
}: {
  objektId: string;
  abteilungen: Abteilung[];
  onAbbruch: () => void;
  onFertig: () => void;
}) {
  const { t } = useTranslation();
  const [abteilungId, setAbteilungId] = useState(abteilungen[0]?.id ?? "");
  /*
   * Eingegeben werden Stunden, gespeichert werden Minuten. Niemand denkt in
   * "240 Minuten Garten", aber die Datenbank soll nicht mit Kommazahlen
   * rechnen muessen.
   */
  const [stundenText, setStundenText] = useState("1");
  const [gueltigVon, setGueltigVon] = useState("");
  const [gueltigBis, setGueltigBis] = useState("");
  /** Leer heisst frei planbar. Das bleibt der Normalfall. */
  const [festerTag, setFesterTag] = useState("");
  const [festeZeit, setFesteZeit] = useState("");
  /*
   * Rhythmus und die Woche, ab der gezaehlt wird. Vorbelegt mit dieser Woche,
   * damit der haeufigste Fall ("ab jetzt abwechselnd") ein Klick ist.
   */
  const [rhythmus, setRhythmus] = useState("1");
  const [rhythmusAb, setRhythmusAb] = useState(() => montagDerWoche(new Date()));
  const [notiz, setNotiz] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const minuten = Math.round(Number(stundenText.replace(",", ".")) * 60);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post(`/properties/${objektId}/leistungen`, {
        abteilungId,
        minutenProWoche: minuten,
        gueltigVon: gueltigVon || null,
        gueltigBis: gueltigBis || null,
        festerTag: festerTag ? Number(festerTag) : null,
        // Ohne festen Tag ist eine Uhrzeit keine Festlegung, sondern eine
        // Falle: sie sieht verbindlich aus und gilt an jedem Tag.
        festeZeit: festerTag ? (festeZeit || null) : null,
        rhythmusWochen: Number(rhythmus),
        // Ohne Rhythmus ueber einer Woche hat der Anker nichts zu sagen.
        rhythmusAb: Number(rhythmus) > 1 ? rhythmusAb : null,
        notiz: notiz || null
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="mt-4 border-t border-linie pt-4">
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("plan.abteilung")}>
          <Auswahl value={abteilungId} onChange={(e) => setAbteilungId(e.target.value)}>
            {abteilungen.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("leistung.stundenProWoche")} hinweis={t("leistung.stundenHinweis")}>
          <Eingabe
            type="number" min={0.25} max={168} step={0.25}
            value={stundenText} onChange={(e) => setStundenText(e.target.value)}
          />
        </Feld>
        <Feld label={t("leistung.von")} hinweis={t("leistung.zeitraumHinweis")}>
          <Eingabe type="date" value={gueltigVon} onChange={(e) => setGueltigVon(e.target.value)} />
        </Feld>
        <Feld label={t("leistung.bis")}>
          <Eingabe type="date" value={gueltigBis} onChange={(e) => setGueltigBis(e.target.value)} />
        </Feld>
        <Feld label={t("leistung.festerTag")} hinweis={t("leistung.festerTagHinweis")}>
          <Auswahl value={festerTag} onChange={(e) => setFesterTag(e.target.value)}>
            <option value="">{t("leistung.freiPlanbar")}</option>
            {WOCHENTAGE.map((d) => (
              <option key={d} value={d}>{t(`wochentag.${d}`)}</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("leistung.festeZeit")} hinweis={t("leistung.festeZeitHinweis")}>
          <Eingabe
            type="time" step={300} value={festeZeit} disabled={!festerTag}
            onChange={(e) => setFesteZeit(e.target.value)}
          />
        </Feld>
        <Feld label={t("leistung.rhythmus")} hinweis={t("leistung.rhythmusHinweis")}>
          <Auswahl value={rhythmus} onChange={(e) => setRhythmus(e.target.value)}>
            {RHYTHMEN.map((n) => (
              <option key={n} value={n}>
                {n === 1 ? t("leistung.jedeWoche") : t("leistung.alleWochen", { wochen: n })}
              </option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("leistung.rhythmusAb")} hinweis={t("leistung.rhythmusAbHinweis")}>
          <Eingabe
            type="date" value={rhythmusAb} disabled={rhythmus === "1"}
            onChange={(e) => setRhythmusAb(e.target.value)}
          />
        </Feld>
        <Feld label={t("plan.notiz")} breit>
          <Eingabe value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-3 flex gap-2">
        <Knopf
          disabled={laeuft || !abteilungId || !Number.isFinite(minuten) || minuten < 1}
          onClick={() => void speichern()}
        >
          {t("aktion.speichern")}
        </Knopf>
        <Knopf art="neben" onClick={onAbbruch}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </div>
  );
}

function kurzesDatum(wert: string, sprache: string): string {
  /*
   * Mittags und nicht Mitternacht: die Datenbank liefert das Datum als
   * Zeitstempel in UTC, und westlich von Greenwich waere Mitternacht schon der
   * Vortag. Der Anker einer Woche, der einen Tag daneben steht, waere genau
   * die Sorte Fehler, die niemand sucht.
   */
  return new Date(`${wert.slice(0, 10)}T12:00:00Z`).toLocaleDateString(sprache, {
    day: "2-digit", month: "2-digit", year: "numeric"
  });
}

function zeitraum(
  von: string | null, bis: string | null,
  sprache: string, t: (schluessel: string, werte?: Record<string, unknown>) => string
): string {
  const kurz = (wert: string) => kurzesDatum(wert, sprache);
  if (von && bis) return `${kurz(von)} bis ${kurz(bis)}`;
  if (von) return t("leistung.abDatum", { datum: kurz(von) });
  return t("leistung.bisDatum", { datum: kurz(bis as string) });
}
